import { Suspense, useContext, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  ClaimScoreLoader,
  ErrorBoundary,
  NavigationHistoryProvider,
  Spacing,
  styled,
  themeMediaQuery,
} from 'ui';
import { ErrorBoundaryPage } from '@components/ErrorBoundaryPage/ErrorBoundaryPage';
import { AuthContext } from '@context/AuthContext';
import { LayoutProvider } from '@context/LayoutContext';
import { UserOrgCaseProvider } from '@context/UserOrgCaseContext';
import { useAppLocation } from '@hooks/customHooks/useAppLocation';
import { PATHS } from '@utils/paths';
import { SideNavbar } from './components/SideNavbar/SideNavbar';
import { MainTopNavbar } from './components/TopNavbar/MainTopNavbar';
import { SecondaryTopNavbar } from './components/TopNavbar/SecondaryTopNavbar';

const DashboardLayoutRoot = styled('div')`
  width: 100%;
  height: 100vh;
  overflow: auto;
  position: fixed;
`;

const StyledMain = styled('main', {
  shouldForwardProp: (prop) => prop !== 'shouldHideSecondNavBar',
})<{ shouldHideSecondNavBar: boolean }>`
  width: 100%;
  height: fit-content;
  max-width: 800px;
  margin: 0 auto;
  padding: ${({ theme }) =>
    `${theme.spacing(Spacing.XLarge)} ${theme.spacing(Spacing.Large)}`};
  padding-top: 88px;
  ${({ theme, shouldHideSecondNavBar }) => themeMediaQuery(theme, 'md')`
    padding-top: ${shouldHideSecondNavBar ? '88px' : '176px'};
    max-width: 1200px;
  `}
`;

const MainWrapper = styled('main')`
  ${({ theme }) => themeMediaQuery(theme, 'md')`
    margin-left: 84px;
  `}
`;

interface PrivateLayoutProps {
  readonly withSidebar?: boolean;
  readonly withNavbar?: boolean;
}

export function PrivateLayout({ withSidebar, withNavbar }: PrivateLayoutProps) {
  const { isAuthenticated } = useContext(AuthContext);
  const { shouldShowOrgOrCase } = useAppLocation();
  const { pathname, search } = useLocation();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleOpenSidebar = () => {
    setIsSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
  };

  const handleChangeSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  if (!isAuthenticated) {
    return (
      <Navigate
        state={{ previousUrl: `${pathname}${search}` }}
        to={PATHS.PUBLIC.login}
      />
    );
  }

  return (
    <NavigationHistoryProvider>
      <LayoutProvider>
        <UserOrgCaseProvider>
          <div>
            {withSidebar ? (
              <SideNavbar
                onClose={handleCloseSidebar}
                onMouseOver={handleOpenSidebar}
                open={isSidebarOpen}
              />
            ) : null}
            <DashboardLayoutRoot>
              {withNavbar ? (
                <>
                  <MainTopNavbar toggleSidebar={handleChangeSidebar} />
                  {shouldShowOrgOrCase ? <SecondaryTopNavbar /> : null}
                </>
              ) : null}
              <ErrorBoundary fallback={<ErrorBoundaryPage />}>
                <Suspense fallback={<ClaimScoreLoader />}>
                  <MainWrapper>
                    <StyledMain shouldHideSecondNavBar={!shouldShowOrgOrCase}>
                      <Outlet />
                    </StyledMain>
                  </MainWrapper>
                </Suspense>
              </ErrorBoundary>
            </DashboardLayoutRoot>
          </div>
        </UserOrgCaseProvider>
      </LayoutProvider>
    </NavigationHistoryProvider>
  );
}
